import type PlayerClass from 'player/javascripts/player'
import type { AutoplayClearance } from 'player/javascripts/player'
import type { AdsResponse } from 'player/javascripts/player/advertising'
import type { AdButtonLabels } from 'player/javascripts/player/videojs/ui/ad-controls'
import PromiseCancellation from 'shared/javascripts/util/promise_cancelation'
import { trackEvent } from './analytics'

type PlayerInstance = InstanceType<typeof PlayerClass>

interface PlayerInitOptions {
  /** See `Player`'s constructor: whether this player starts by itself, decided by the page. */
  autoplay?: boolean
  /** See `Player`'s `AutoplayClearance`: what an autoplaying player waits for before it starts. */
  autoplayClearance?: AutoplayClearance
  showAds?: boolean
  site?: string
  videoBanners?: HTMLElement
  substituteLogo?: HTMLElement
  preAdScreen?: HTMLElement
  adUnit?: string
  isVideoAdBanner?: boolean
  playerType?: string
  /** See `Player#init`: an ad fetched by the caller instead of one requested by the player. */
  adsResponse?: AdsResponse
  adButtonLabels?: AdButtonLabels
}

export interface PlayerElement extends HTMLElement {
  player?: PlayerInstance
  playerPromise?: Promise<PlayerInstance>
}

export function loadPlayer(
  element: PlayerElement,
  initOptions: PlayerInitOptions = {},
): { playerPromise: Promise<PlayerInstance>; destroyPlayer: () => Promise<void> } {
  let destroyed = false

  async function loadAndInit(): Promise<PlayerInstance> {
    const { default: Player } = await import(
      /* webpackChunkName: "player" */ 'player/javascripts/player'
    )
    if (destroyed) {
      return Promise.reject(new PromiseCancellation())
    } else {
      const player = new Player(element, {
        // Bound to the container: whatever tracking context surrounds the player (a block's video, the
        // learn session) is on every event it emits.
        trackEvent: (name, data) => trackEvent(name, data, { from: element }),
        autoplay: initOptions.autoplay,
        autoplayClearance: initOptions.autoplayClearance,
      })

      await player.init(initOptions)
      element.player = player
      return player
    }
  }

  const playerPromise = loadAndInit()
  element.playerPromise = playerPromise

  return {
    async destroyPlayer() {
      destroyed = true
      const player = await playerPromise
      player.destroy()
    },
    playerPromise,
  }
}
